import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { auth } from "../firebase";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import "../styles/theme.css";
import "./Payment.css";
import Footer from "../components/Footer";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
); 

const ENDPOINT = process.env.REACT_APP_EMAIL_ENDPOINT;
const PAYMENT_INTENT_ENDPOINT =
  process.env.REACT_APP_STRIPE_PAYMENT_INTENT_ENDPOINT;
const TO_EMAIL = process.env.REACT_APP_CONTACT_TO;
const FROM_NAME = process.env.REACT_APP_FROM_NAME || "CRIGO Technologies AB";
const FROM_ADDR =
  process.env.REACT_APP_FROM_EMAIL || "noreply@crigotechnologies.se";

function escapeHtml(str) {
  return String(str || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

const formatSek = (amount) =>
  new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
  }).format(Number(amount || 0));

const sendEmail = async ({ to, subject, html }) => {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      from: `${FROM_NAME} <${FROM_ADDR}>`,
      to,
      subject,
      html,
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.ok) {
    throw new Error(data?.error || "Email failed to send");
  }
};

const sendPurchaseEmail = async ({
  orderId,
  type,
  selectedPlan,
  selectedAddOns,
  totalPrice,
  billingCycle,
  includeHosting,
  hostingDuration,
  userDetails,
  paymentMethod,
  renewalDate,
}) => {
  if (!ENDPOINT || !TO_EMAIL || !userDetails?.email) {
    console.warn("Email endpoint, admin email, or customer email is missing.");
    return;
  }

  const addOnsHtml =
    selectedAddOns && selectedAddOns.length > 0
      ? selectedAddOns
          .map(
            (addon) =>
              `<li>${escapeHtml(addon.name)} - ${escapeHtml(formatSek(addon.price))}</li>`
          )
          .join("")
      : "<li>No add-ons selected</li>";

  const typeTitle =
    type === "one-time" ? "One-Time Website Purchase" : "Subscription";

    const adminHtml = `
    <div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6">
      <h2>New ${typeTitle}</h2>
  
      <p><strong>Order ID:</strong> ${escapeHtml(orderId)}</p>
      <p><strong>Customer Name:</strong> ${escapeHtml(userDetails?.name)}</p>
      <p><strong>Customer Email:</strong> ${escapeHtml(userDetails?.email)}</p>
  
      <hr/>
  
      <p><strong>Plan:</strong> ${escapeHtml(selectedPlan)}</p>
      <p><strong>Total Price:</strong> ${escapeHtml(formatSek(totalPrice))}</p>
      <p><strong>Payment Method:</strong> ${escapeHtml(paymentMethod)}</p>
  
      ${
        type === "subscription"
          ? `<p><strong>Billing Cycle:</strong> ${escapeHtml(billingCycle)}</p>
             <p><strong>Renewal Date:</strong> ${escapeHtml(renewalDate)}</p>`
          : `<p><strong>Hosting:</strong> ${
              includeHosting
                ? `${escapeHtml(hostingDuration)} year(s)`
                : "Not included"
            }</p>`
      }
  
      <p><strong>Add-ons:</strong></p>
      <ul>${addOnsHtml}</ul>
    </div>
  `;
  
  const LOGO_URL =
    "https://res.cloudinary.com/dc0g8yyux/image/upload/v1777839674/Crigo_Tech_Logo_xqary8.png";
  
  const customerHtml = `
    <div style="font-family:Arial,Helvetica,sans-serif;background:#f4f6fb;padding:20px;">
  
      <div style="max-width:620px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 6px 20px rgba(0,0,0,0.08);">
  
        <div style="background:#0a0f2c;color:#ffffff;padding:28px 22px;text-align:center;">
          <img 
            src="${LOGO_URL}" 
            alt="CRIGO Technologies AB" 
            style="width:90px;height:auto;margin-bottom:12px;border-radius:10px;box-shadow:0 4px 10px rgba(0,0,0,0.15);"
          />
  
          <h1 style="margin:0;font-size:24px;letter-spacing:0.5px;">
            CRIGO Technologies AB
          </h1>
  
          <p style="margin:8px 0 0;font-size:14px;color:#d7dcff;">
            Building digital experiences that scale
          </p>
        </div>
  
        <div style="padding:28px 24px;color:#222;">
  
          <h2 style="margin:0 0 10px;color:#0a0f2c;font-size:22px;">
            Thank you, ${escapeHtml(userDetails?.name)}
          </h2>
  
          <p style="font-size:13px;color:#666;margin:0 0 18px;">
            Order ID: <strong>${escapeHtml(orderId)}</strong>
          </p>
  
          <p style="font-size:15px;line-height:1.7;margin:0 0 18px;">
            Your ${
              type === "one-time" ? "website purchase" : "subscription"
            } has been <strong>successfully confirmed</strong> and is now being processed.
          </p>
  
          <div style="background:#eef3ff;border-left:4px solid #0110FC;padding:14px 16px;border-radius:6px;margin:20px 0;">
            <p style="margin:0;font-size:14px;line-height:1.6;">
              Our team will contact you within <strong>24–48 hours</strong> to begin your project setup.
            </p>
          </div>
  
          <h3 style="font-size:17px;margin:24px 0 12px;color:#0a0f2c;">
            Order Summary
          </h3>
  
          <div style="background:#f8f9fc;padding:18px;border-radius:8px;border:1px solid #e5e8f0;">
            <p style="margin:8px 0;"><strong>Plan:</strong> ${escapeHtml(selectedPlan)}</p>
            <p style="margin:8px 0;"><strong>Total Price:</strong> ${escapeHtml(formatSek(totalPrice))}</p>
  
            ${
              type === "subscription"
                ? `<p style="margin:8px 0;"><strong>Billing Cycle:</strong> ${escapeHtml(billingCycle)}</p>
                   <p style="margin:8px 0;"><strong>Renewal Date:</strong> ${escapeHtml(renewalDate)}</p>`
                : `<p style="margin:8px 0;"><strong>Hosting:</strong> ${
                    includeHosting
                      ? `${escapeHtml(hostingDuration)} year(s)`
                      : "Not included"
                  }</p>`
            }
  
            <p style="margin:14px 0 6px;"><strong>Add-ons:</strong></p>
            <ul style="margin:5px 0 0 20px;padding:0;">
              ${addOnsHtml}
            </ul>
          </div>
  
          <h3 style="font-size:17px;margin:26px 0 12px;color:#0a0f2c;">
            What happens next?
          </h3>
  
          <div style="font-size:14px;line-height:1.7;color:#333;">
            <p style="margin:6px 0;">✅ We review your order details.</p>
            <p style="margin:6px 0;">✅ We contact you to confirm your requirements.</p>
            <p style="margin:6px 0;">✅ We begin the setup process for your project.</p>
          </div>
  
          <div style="margin-top:28px;text-align:center;padding-top:22px;border-top:1px solid #e5e8f0;">
            <p style="color:#555;font-size:14px;margin-bottom:15px;">
              Need help or have questions?
            </p>
  
            <a 
              href="mailto:info@crigotechnologies.se" 
              style="display:inline-block;background:#0110FC;color:#ffffff;padding:13px 22px;border-radius:7px;text-decoration:none;font-weight:bold;font-size:14px;"
            >
              Contact Support
            </a>
  
            <div style="margin-top:20px;color:#555;font-size:13px;line-height:1.7;">
              <p style="margin:5px 0;">
                📧 <a href="mailto:info@crigotechnologies.se" style="color:#0110FC;text-decoration:none;">
                  info@crigotechnologies.se
                </a>
              </p>
  
              <p style="margin:5px 0;">
                📞 <a href="tel:+4687455557" style="color:#0110FC;text-decoration:none;">
                  +46 8-745 55 57
                </a>
              </p>
  
              <p style="margin:5px 0;">
                📞 <a href="tel:+46737637460" style="color:#0110FC;text-decoration:none;">
                  +46 73-763 74 60
                </a>
              </p>
            </div>
          </div>
  
          <p style="margin-top:28px;font-size:14px;color:#333;line-height:1.6;">
            Best regards,<br/>
            <strong>CRIGO Technologies AB</strong>
          </p>
        </div>
  
        <div style="background:#0a0f2c;color:#bfc6ff;text-align:center;padding:16px;font-size:12px;line-height:1.6;">
          <p style="margin:0;">
            © ${new Date().getFullYear()} CRIGO Technologies AB. All rights reserved.
          </p>
          <p style="margin:5px 0 0;">
            This is an automated confirmation email. Please contact us using the details above.
          </p>
        </div>
  
      </div>
    </div>
  `;
  await sendEmail({
    to: TO_EMAIL,
    subject:
      type === "one-time"
        ? `New one-time website purchase from ${userDetails?.name}`
        : `New subscription from ${userDetails?.name}`,
    html: adminHtml,
  });

  await sendEmail({
    to: userDetails.email,
    subject:
      type === "one-time"
        ? "Your website purchase has been received"
        : "Your subscription has been confirmed",
    html: customerHtml,
  });
};

const Payment = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    selectedPlan,
    selectedAddOns = [],
    totalPrice,
    selectedPayment,
    billingCycle,
    includeHosting,
    hostingDuration,
    isOneTime = false,
    userDetails,
  } = location.state || {};

  const paymentLabelMap = {
    stripe: t("pricing.methods.card"),
    paypal: t("pricing.methods.paypal"),
    bank: t("pricing.methods.bank"),
  };

  const paymentLabel = selectedPayment ? paymentLabelMap[selectedPayment] : "";

  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedPlan) {
      navigate("/checkout");
    }
  }, [selectedPlan, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!userDetails || !userDetails.name || !userDetails.email) {
      setErrorMessage(t("payment.missing_user_details"));
      return;
    }
  
    if (!currentUser || !currentUser.uid) {
      setErrorMessage("User not authenticated");
      return;
    }
  
    if (!stripe || !elements) {
      setErrorMessage("Stripe is not ready yet. Please try again.");
      return;
    }
  
    if (!PAYMENT_INTENT_ENDPOINT) {
      setErrorMessage("Payment endpoint is missing.");
      return;
    }
  
    setIsProcessing(true);
    setErrorMessage("");
  
    try {
      const cardElement = elements.getElement(CardElement);
  
      if (!cardElement) {
        setErrorMessage("Card details are missing.");
        setIsProcessing(false);
        return;
      }
  
      const userId = currentUser.uid;
      const orderId = `CRIGO-${new Date().getFullYear()}-${Math.floor(
        1000 + Math.random() * 9000
      )}`;
  
      const paymentIntentRes = await fetch(PAYMENT_INTENT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: totalPrice,
          currency: "sek",
          metadata: {
            orderId,
            userId,
            customerEmail: userDetails.email,
            plan: selectedPlan || "One-Time Website",
            type: isOneTime ? "one-time" : "subscription",
          },
        }),
      });
  
      const paymentIntentData = await paymentIntentRes.json().catch(() => ({}));
  
      if (
        !paymentIntentRes.ok ||
        !paymentIntentData.ok ||
        !paymentIntentData.clientSecret
      ) {
        throw new Error(paymentIntentData?.error || "Could not start payment.");
      }
  
      const { paymentIntent, error } = await stripe.confirmCardPayment(
        paymentIntentData.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: userDetails.name,
              email: userDetails.email,
            },
          },
        }
      );
  
      if (error) {
        throw new Error(error.message || "Payment was not successful.");
      }
  
      if (paymentIntent.status !== "succeeded") {
        throw new Error(`Payment status: ${paymentIntent.status}`);
      }
  
      const paymentMethod = {
        id: paymentIntent.payment_method,
        type: "card",
        last4: paymentIntent.charges?.data?.[0]?.payment_method_details?.card?.last4 || "",
      };
  
      if (isOneTime) {
        const ordersRef = collection(db, "users", userId, "orders");
  
        await addDoc(ordersRef, {
          orderId,
          userId,
          plan: selectedPlan || "One-Time Website",
          addOns: selectedAddOns || [],
          totalPrice,
          currency: "sek",
          hosting: includeHosting
            ? `${hostingDuration} year(s)`
            : "Not included",
          userEmail: userDetails.email,
          userName: userDetails.name,
          stripePaymentIntentId: paymentIntent.id,
          stripePaymentMethodId: paymentMethod.id,
          paymentMethod: paymentMethod.type,
          last4: paymentMethod.last4,
          createdAt: new Date(),
          status: "completed",
          isOneTime: true,
        });
  
        await sendPurchaseEmail({
          orderId,
          type: "one-time",
          selectedPlan: selectedPlan || "One-Time Website",
          selectedAddOns,
          totalPrice,
          includeHosting,
          hostingDuration,
          userDetails,
          paymentMethod: paymentMethod.type,
        });
      } else {
        const today = new Date();
        const renewalDate = new Date(today);
  
        if (billingCycle === "monthly") {
          renewalDate.setMonth(today.getMonth() + 1);
        } else {
          renewalDate.setFullYear(today.getFullYear() + 1);
        }
  
        const formattedRenewalDate = renewalDate.toISOString().split("T")[0];
  
        const subscriptionRef = collection(
          db,
          "users",
          userId,
          "subscriptions"
        );
  
        await addDoc(subscriptionRef, {
          orderId,
          plan: selectedPlan,
          billingCycle,
          addOns: selectedAddOns || [],
          totalPrice,
          currency: "sek",
          paymentMethod: paymentMethod.type,
          userEmail: userDetails.email,
          userName: userDetails.name,
          stripePaymentIntentId: paymentIntent.id,
          stripePaymentMethodId: paymentMethod.id,
          last4: paymentMethod.last4,
          createdAt: new Date(),
          renewalDate: formattedRenewalDate,
          active: true,
        });
  
        await sendPurchaseEmail({
          orderId,
          type: "subscription",
          selectedPlan,
          selectedAddOns,
          totalPrice,
          billingCycle,
          userDetails,
          paymentMethod: paymentMethod.type,
          renewalDate: formattedRenewalDate,
        });
      }
  
      navigate("/confirmation", {
        state: {
          selectedPlan,
          selectedAddOns,
          totalPrice,
          selectedPayment,
          paymentMethod: "Stripe",
          userDetails,
          isOneTime,
          includeHosting,
          hostingDuration,
          orderId,
        },
      });
    } catch (err) {
      console.error("❌ Payment error:", err.message || err);
      setErrorMessage(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="payment-case">
      <div className="main-content">
        <h1>{t("payment.title")}</h1>
        <p>{t("payment.enter_details")}</p>

        {paymentLabel && (
          <p style={{ marginTop: 8 }}>
            {t("checkout.payment_method")}: {paymentLabel}
          </p>
        )}

        <form className="payment-form" onSubmit={handleSubmit}>
          <CardElement className="card-element" />

          {errorMessage && <p className="error-message">{errorMessage}</p>}

          <button type="submit" className="pay-btn" disabled={isProcessing}>
  {isProcessing ? t("payment.processing") : t("payment.pay_now")}
</button>
        </form>
      </div>

      <Footer />
    </div>
  );
};

const PaymentWrapper = () => (
  <Elements stripe={stripePromise}>
    <Payment />
  </Elements>
);

export default PaymentWrapper;